import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks, ArrowLeft, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const STATUS_LABELS = {
  ANSWERED: "Risposto",
  CONFIRMED: "Confermato",
  TO_VERIFY: "Da verificare",
  IMPORTED: "Importato",
  DA_FONTI_ESISTENTI: "Da fonte esistente",
};

function renderValue(v) {
  if (v === null || v === undefined) return "—";
  if (v === true) return "Sì";
  if (v === false) return "No";
  if (v === "UNKNOWN") return "Non so";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export default function QuestionnaireReview() {
  const { companyId, period, can } = useTenant();
  const [groups, setGroups] = useState([]);
  const [showTech, setShowTech] = useState(false);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/questionnaire/review`);
      setGroups(data.groups);
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="questionnaire-review-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/questionario" className="inline-flex items-center gap-1.5 text-sm text-green-700 font-semibold hover:underline mb-2" data-testid="back-to-questionnaire">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Torna al questionario
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <ListChecks className="w-7 h-7 text-green-700" aria-hidden="true" />
            Rivedi le risposte
          </h1>
          <p className="text-sm text-slate-600 mt-1">Esercizio {period?.year} — tutte le risposte date, raggruppate per area.</p>
        </div>
        {can("esg:verify") && (
          <Button variant="outline" size="sm" onClick={() => setShowTech(!showTech)} data-testid="toggle-tech-review">
            {showTech ? "Nascondi" : "Mostra"} dettagli VSME
          </Button>
        )}
      </div>

      {groups.length === 0 && (
        <Card className="border-slate-200" data-testid="review-empty">
          <CardContent className="p-6 text-sm text-slate-600">
            Nessuna risposta registrata per questo periodo.{" "}
            <Link to="/questionario" className="text-green-700 font-semibold hover:underline">Inizia il questionario</Link>.
          </CardContent>
        </Card>
      )}

      {groups.map((g) => (
        <Card key={g.section_code} className="border-slate-200" data-testid={`review-section-${g.section_code.toLowerCase()}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{g.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {g.items.map((item) => (
              <div key={item.question_code} className="flex flex-wrap items-start justify-between gap-2 border border-slate-100 rounded-md p-3"
                data-testid={`review-item-${item.question_code}`}>
                <div className="max-w-xl">
                  <p className="text-sm font-medium text-slate-900">{item.question_text}</p>
                  <p className="text-sm text-slate-700 mt-0.5" data-testid={`review-value-${item.question_code}`}>
                    {renderValue(item.answer_value)}
                    {item.existing_values?.map((ev) => (
                      <span key={ev.datapoint_code} className="block text-sky-800">
                        {renderValue(ev.value)}{ev.unit ? ` ${ev.unit}` : ""} — fonte {ev.source_type}
                      </span>
                    ))}
                  </p>
                  {showTech && (item.datapoint_codes || []).length > 0 && (
                    <p className="text-xs font-mono text-slate-500 mt-1">
                      → {(item.datapoint_codes || []).join(", ")}
                    </p>
                  )}
                  {item.issues?.length > 0 && (
                    <div className="mt-1 space-y-1">
                      {item.issues.map((i) => (
                        <p key={i.rule_code} role="alert"
                          className={`text-xs flex items-center gap-1.5 ${
                            i.severity === "ERROR" ? "text-red-700 font-semibold" : "text-amber-700"}`}>
                          {i.message}
                          {i.severity === "ERROR" && (
                            <span className="shrink-0 text-[10px] font-bold uppercase bg-red-100 text-red-800 rounded px-1 py-0.5"
                              data-testid={`review-issue-blocking-${i.rule_code}`}>Bloccante</span>
                          )}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                <Badge variant="outline" className={
                  item.status === "CONFIRMED" ? "bg-green-50 text-green-800 border-green-300" :
                  item.status === "DA_FONTI_ESISTENTI" ? "bg-sky-50 text-sky-800 border-sky-300" :
                  "bg-slate-50 text-slate-700 border-slate-300"
                }>
                  {STATUS_LABELS[item.status] || item.status}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <div className="flex gap-3">
        <Link to="/questionario" data-testid="review-continue">
          <Button className="bg-green-700 hover:bg-green-800">
            Continua il questionario <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
          </Button>
        </Link>
        <Link to="/vsme" data-testid="review-open-vsme">
          <Button variant="outline">Apri vista tecnica VSME</Button>
        </Link>
      </div>
    </div>
  );
}
